import { useAssetVizInternal } from 'features/assetViz/AssetVizInternalProvider'
import {
  BoundsCalculator,
  DefaultCamera,
  FitCalculator,
  MovementCalculator,
  PositionCalculator,
  SelectionCallback,
} from 'features/assetViz/cameras/DefaultCamera'
import { EllipsoidalHeadSurface3D } from 'features/assetViz/objects/surfaces/EllipsoidalHeadSurface3D'
import { HemisphericalHeadSurface3D } from 'features/assetViz/objects/surfaces/HemisphericalHeadSurface3D'
import { PressureVesselShellSurface3D } from 'features/assetViz/objects/surfaces/PressureVesselShellSurface3D'
import { SphereFlatCapSurface3D } from 'features/assetViz/objects/surfaces/SphereFlatCapSurface3D'
import { TorisphericalHeadSurface3D } from 'features/assetViz/objects/surfaces/TorisphericalHeadSurface3D'
import { SceneFactoryConfig } from 'features/assetViz/scenes/sceneFactory'
import React from 'react'
import * as THREE from 'three'
import {
  GeckoAssetModel,
  GeckoAssetModelType,
  GeckoComponentModelType,
  GeckoComponentRect,
  GeckoEditableComponent,
  GeckoEllipsoidalHead,
  GeckoHemisphericalHead,
  GeckoMeasurementUnit,
  GeckoPressureVesselShell,
  GeckoSphereFlatCap,
  GeckoTorisphericalHead,
  PressureVesselShellOrientation,
} from 'types'

export const PressureVesselSceneConfig: SceneFactoryConfig = {
  name: 'PressureVessel',
  assetModels: [GeckoAssetModelType.PressureVessel],
  componentModels: [
    GeckoComponentModelType.PressureVesselShell,
    GeckoComponentModelType.HemisphericalHead,
    GeckoComponentModelType.TorisphericalHead,
    GeckoComponentModelType.EllipsoidalHead,
    GeckoComponentModelType.SphereFlatCap,
  ],
  sceneFunc: (props) => <PressureVesselScene {...props} />,
}

/**
 * A scene depicting pressure vessel components including shells and various head types.
 * Consolidates all pressure vessel rendering into a single scene following the
 * ContainmentDomeScene pattern. Handles different coordinate systems and camera
 * positioning based on component type.
 * @param componentModel  The pressure vessel component model (shell or head)
 */
export const PressureVesselScene: React.FC<{
  componentModel: GeckoEditableComponent
  assetModel: GeckoAssetModel
}> = ({ componentModel, assetModel }) => {
  const selectedModel = componentModel as
    | GeckoPressureVesselShell
    | GeckoHemisphericalHead
    | GeckoTorisphericalHead
    | GeckoEllipsoidalHead
    | GeckoSphereFlatCap

  //----------------------------------------------------------------------------
  const { show3D, componentDataFrames } = useAssetVizInternal()

  const componentBounds = React.useMemo(() => {
    // Different component types use different coordinate systems
    if (selectedModel.type === GeckoComponentModelType.PressureVesselShell) {
      // Shell uses original bounds (X = circumference, Y = length)
      const componentBounds = selectedModel.componentBounds
      if (
        componentBounds.y.unit !== GeckoMeasurementUnit.Meters ||
        componentBounds.x.unit !== GeckoMeasurementUnit.Meters
      )
        throw new Error(
          `X and y measurement units should be in meters, but
                the x range is in ${componentBounds.x.unit.id}
                and y range is in ${componentBounds.y.unit.id}`
        )
      return componentBounds
    } else if (selectedModel.type === GeckoComponentModelType.EllipsoidalHead) {
      // Ellipsoidal heads use radius/minor radius for bounds
      const radius = Math.max(
        selectedModel.radius.num,
        selectedModel.minorRadius.num
      )
      return show3D
        ? selectedModel.componentBounds
        : GeckoComponentRect.CreateFromXYs(
            selectedModel.componentBounds.componentID,
            -radius,
            -radius, // Use minor radius for height bounds
            radius,
            radius,
            selectedModel.componentBounds.y.unit,
            selectedModel.componentBounds.y.unit
          )
    } else if (
      selectedModel.type === GeckoComponentModelType.TorisphericalHead
    ) {
      // Torispherical heads use crown radius for bounds
      const radius = selectedModel.crownRadius.num
      return GeckoComponentRect.CreateFromXYs(
        selectedModel.componentBounds.componentID,
        -radius,
        -radius,
        radius,
        radius,
        selectedModel.componentBounds.x.unit,
        selectedModel.componentBounds.y.unit
      )
    } else {
      // Hemispherical heads use radius for spherical bounds
      const radius = selectedModel.radius.num
      return GeckoComponentRect.CreateFromXYs(
        selectedModel.componentBounds.componentID,
        -radius,
        -radius,
        radius,
        radius,
        selectedModel.componentBounds.x.unit,
        selectedModel.componentBounds.y.unit
      )
    }
  }, [selectedModel, show3D])

  const componentRadius = React.useMemo(() => {
    // Get the primary radius for each component type
    if (selectedModel.type === GeckoComponentModelType.PressureVesselShell) {
      return selectedModel.radius
    } else if (
      selectedModel.type === GeckoComponentModelType.TorisphericalHead
    ) {
      return selectedModel.crownRadius
    } else if (selectedModel.type === GeckoComponentModelType.EllipsoidalHead) {
      return selectedModel.radius
    } else {
      return selectedModel.radius
    }
  }, [selectedModel])

  const onComponentSelected = React.useCallback<SelectionCallback>(
    () =>
      // Start camera where there's data to look at
      selectedModel.type === GeckoComponentModelType.EllipsoidalHead && !show3D
        ? componentBounds
        : (componentDataFrames
            ?.at(0)
            ?.data?.geometry?.project(componentBounds.space) ??
          componentBounds),
    [componentDataFrames, componentBounds, selectedModel, show3D]
  )

  const getCameraBounds = React.useCallback<BoundsCalculator>(
    () => ({
      rect: componentBounds,
      doWrapX: show3D, // All pressure vessel components can wrap in 3D
    }),
    [componentBounds, show3D]
  )

  const getCameraPosition = React.useCallback<PositionCalculator>(
    ({ componentX, componentY, distToComponent }) => {
      const relX = componentX - componentBounds.start.x.num
      const relY = componentY - componentBounds.start.y.num

      if (!show3D) {
        if (selectedModel.type === GeckoComponentModelType.EllipsoidalHead) {
          return {
            position: [componentX, componentY, distToComponent],
            rotation: [0, 0, 0],
          }
        }
        return {
          position: [relX, relY, distToComponent],
          rotation:
            selectedModel.type ===
              GeckoComponentModelType.PressureVesselShell &&
            selectedModel.orientation ===
              PressureVesselShellOrientation.Horizontal
              ? [0, 0, 0.5 * Math.PI]
              : [0, 0, 0],
        }
      }

      // Different 3D positioning based on component type
      if (selectedModel.type === GeckoComponentModelType.PressureVesselShell) {
        // Shell uses cylindrical coordinates (pipe-like)
        const angle = 2.0 * Math.PI * (relX / componentBounds.width.num - 0.25)
        const distToAxis = componentRadius.num + distToComponent

        return {
          position: [
            distToAxis * Math.sin(angle),
            relY,
            distToAxis * Math.cos(angle),
          ],
          rotation: [
            0,
            angle,
            selectedModel.orientation ===
            PressureVesselShellOrientation.Horizontal
              ? 0.5 * Math.PI
              : 0,
          ], // Match pipe rotation
        }
      } else if (
        selectedModel.type === GeckoComponentModelType.EllipsoidalHead
      ) {
        const radius = Math.max(
          selectedModel.radius.num,
          selectedModel.minorRadius.num
        )
        const omega = componentY / radius
        // Ellipsoidal head uses elliptical positioning
        const phi = (componentX / 180) * 21 * Math.PI - Math.PI / 2

        const distToAxis = radius + distToComponent

        const x = distToAxis * Math.sin(omega) * Math.sin(phi)
        const y = distToAxis * Math.cos(-omega)
        const z = distToAxis * Math.sin(omega) * Math.cos(phi)

        const cameraMatrixX = new THREE.Matrix4().makeRotationX(
          omega - Math.PI / 2
        )
        const cameraMatrixY = new THREE.Matrix4().makeRotationY(phi)
        const cameraMatrix = new THREE.Matrix4()
          .multiply(cameraMatrixY)
          .multiply(cameraMatrixX)
        const quaternion = new THREE.Quaternion().setFromRotationMatrix(
          cameraMatrix
        )
        const euler = new THREE.Euler().setFromQuaternion(quaternion)

        return {
          position: [x, y, z],
          rotation: [euler.x, euler.y, euler.z],
        }
      } else {
        // Hemispherical and Torispherical heads use spherical positioning (dome-like)
        const angle =
          2.0 *
            Math.PI *
            ((componentX + componentRadius.num) / (componentRadius.num * 2)) -
          Math.PI / 2 // Add 90 degrees offset like dome

        const distToAxis = componentRadius.num + distToComponent

        return {
          position: [
            Math.sin(angle) * distToAxis,
            componentY,
            Math.cos(angle) * distToAxis,
          ],
          rotation: [0, angle, 0],
        }
      }
    },
    [componentBounds, componentRadius.num, show3D, selectedModel]
  )

  const getMovementForCursorDelta = React.useCallback<MovementCalculator>(
    ([x, y]) => {
      // Only shell components need special movement (pipe-like)
      if (
        selectedModel.type === GeckoComponentModelType.PressureVesselShell &&
        selectedModel.orientation === PressureVesselShellOrientation.Horizontal
      ) {
        return [y, -x] // Match pipe movement pattern
      }
      if (
        selectedModel.type === GeckoComponentModelType.EllipsoidalHead &&
        show3D
      ) {
        return [x, -y]
      }
      // Head components use default movement
      return [x, y]
    },
    [selectedModel, show3D]
  )

  const getCameraDimensionsFit = React.useCallback<FitCalculator>(
    (rect) => {
      if (!show3D) {
        return [rect.width.num, rect.height.num]
      }

      // Different fit calculations based on component type
      if (selectedModel.type === GeckoComponentModelType.PressureVesselShell) {
        return [
          Math.min(rect.width.num, componentRadius.num * 2.0),
          rect.height.num,
        ]
      } else if (
        selectedModel.type === GeckoComponentModelType.EllipsoidalHead
      ) {
        return [
          Math.min(rect.width.num, componentRadius.num * 2.0),
          selectedModel.minorRadius.num * 2.0,

          // Use minor radius for height fit
        ]
      } else {
        // Hemispherical and Torispherical heads
        return [
          Math.min(rect.width.num, componentRadius.num * 2.0),
          rect.height.num,
        ]
      }
    },
    [show3D, componentRadius, selectedModel]
  )

  const cameraKey = React.useMemo(
    () =>
      selectedModel.type === GeckoComponentModelType.EllipsoidalHead
        ? show3D
          ? 'ellipsoidalHead3D'
          : 'ellipsoidalHead2D'
        : '',
    [selectedModel, show3D]
  )

  //----------------------------------------------------------------------------

  return !selectedModel ? null : (
    <>
      <DefaultCamera
        key={cameraKey}
        onComponentSelected={onComponentSelected}
        getCameraBounds={getCameraBounds}
        getCameraPosition={getCameraPosition}
        getMovementForCursorDelta={getMovementForCursorDelta}
        getCameraDimensionsFit={getCameraDimensionsFit}
      />
      {selectedModel instanceof GeckoPressureVesselShell ? (
        <PressureVesselShellSurface3D model={selectedModel} />
      ) : selectedModel instanceof GeckoHemisphericalHead ? (
        <HemisphericalHeadSurface3D model={selectedModel} />
      ) : selectedModel instanceof GeckoTorisphericalHead ? (
        <TorisphericalHeadSurface3D model={selectedModel} />
      ) : selectedModel instanceof GeckoEllipsoidalHead ? (
        <EllipsoidalHeadSurface3D model={selectedModel} />
      ) : selectedModel instanceof GeckoSphereFlatCap ? (
        <SphereFlatCapSurface3D model={selectedModel} assetModel={assetModel} />
      ) : null}
    </>
  )
}
